import { graphql, rest } from 'msw';
import { MoneyRequestStatus, PayAnyoneStatus } from 'types';

export const buildTokenInfo = (token: string) => (bank: string) => (
  status: string
) => ({
  token: token,
  amount: '20.7',
  memo: 'test again with local bank account service',
  status: status,
  expiresAt: '2020-11-13T20:29:34.000Z',
  publicId: Math.random().toString(36),
  receiverIdentifier: 'hatsam1@gmail.com',
  claimWithChime: true,
  sender: {
    id: 938,
    bank,
    name: 'Hat Sam',
    referralToken: 'hatsam1',
    referralFinePrint: null,
  },
  recipientPreviouslyClaimed: false,
  flipperFlags: {
    ocrEnabled: true,
  },
  maintenanceMode: false,
});

let numberOfCalls = 0;
let isFormSubmitted = false;

const claimInfoPostResolver = ({ body, params: { token } }, res, ctx) => {
  const caseValue = Number(token) >= 11111 ? token : body['zip_code'];
  switch (caseValue) {
    case '11111':
      return res(
        ctx.status(422),
        ctx.json({
          error_id: 'claimed_from_expired_status',
          error_header: 'Dang! Payment expired.',
          error_body:
            "This payment is no longer available. Ask your friend to send money again.\nYou can still join Chime, and you'll both get $50 when you set up a qualifying direct deposit of $200+ within 45 days of enrolling.",
        })
      );
    case '22222':
      return res(
        ctx.status(422),
        ctx.json({
          error_id: 'generic',
          error_header: 'Something went wrong',
          error_body:
            'We’re working on it. Please try again later or contact our Member Services team if you need more support.',
        })
      );
    case '33333':
      return res(
        ctx.status(422),
        ctx.json({
          error_id: 'claimed_from_succeeded_status',
          error_header: 'Payment already claimed',
          error_body:
            'This payment has already been claimed. If this wasn’t you, please contact us immediately.',
        })
      );
    case '44444':
      return res(
        ctx.status(422),
        ctx.json({
          error__id: 'claimed_from_nonpending_status',
          error_header: 'Something went wrong',
          error_body:
            'We’re working on it. Please try again later or contact our Member Services team if you need more support.',
        })
      );
    case '55555':
      return res(
        ctx.status(422),
        ctx.json({
          error_id: 'exceeded_limits',
          error_header: 'Limit reached',
          error_body:
            'You can only claim up to $1,000 per month. Please contact our Member Services team if you need more support.',
        })
      );
    case '66666':
      return res(
        ctx.status(422),
        ctx.json({
          error_id: 'debit_card_failed_validation',
          error_header:
            'We couldn’t verify your debit card. Please check the information and try again.',
          error_body: '',
        })
      );
    case '77777':
      return res(
        ctx.status(422),
        ctx.json({
          error_id: 'debit_card_not_eligible',
          error_header:
            'We don’t accept prepaid, credit, international, or Chime cards. Please try again with a U.S. debit card.',
          error_body: '',
        })
      );
    case '88888':
      return res(
        ctx.status(422),
        ctx.json({
          error_id: 'receiver_blacklisted',
          error_header: "We're unable to process this request",
          error_body:
            'Please contact our Member Services team if you need more support.',
        })
      );
    case '99999':
      return res(
        ctx.status(422),
        ctx.json({
          error_id: 'claimed_from_expired_status',
          error_header: 'Dang! Payment expired.',
          error_body:
            "This payment is no longer available. Ask your friend to send money again.\nYou can still join Chime, and you'll both get $50 when you set up a qualifying direct deposit of $200+ within 45 days of enrolling.",
        })
      );
    case '99991':
      return res(ctx.status(500));

    default:
      isFormSubmitted = true;
      return res(
        ctx.status(200),
        ctx.json({
          success: true,
        })
      );
  }
};

export const handlers = [
  rest.get(
    `${process.env.CONSUMER_HOST}/pf/:token.json`,
    ({ params: { token } }, res, ctx) => {
      const buildTokenResponseWithBank = buildTokenInfo(token);
      const strideResponse = buildTokenResponseWithBank('stride');
      const bancorpResponse = buildTokenResponseWithBank('bancorp');
      const tokenWithReuseDebitValues = Number(token) >= 11111 ? '10' : token;

      switch (tokenWithReuseDebitValues) {
        case '1': {
          numberOfCalls++;

          if (isFormSubmitted) {
            const isPollingComplete = numberOfCalls > 3;

            return isPollingComplete
              ? res(ctx.json(strideResponse('succeeded')))
              : res(ctx.json(strideResponse('sent')));
          }

          return res(ctx.json(strideResponse('pending')));
        }
        case '2':
          return res(ctx.json(bancorpResponse('pending')));
        case '3':
          return res(ctx.json(bancorpResponse('expired')));
        case '4':
          return res(ctx.json(bancorpResponse('succeeded')));
        case '5':
          return res(ctx.json(bancorpResponse('cancelled')));
        case '6':
          return res(ctx.json(bancorpResponse('sent')));
        case '7':
          return res(ctx.json(bancorpResponse('failed')));
        case '8':
          return res(ctx.json(bancorpResponse('reversed')));
        case '9': {
          return res(
            ctx.json({
              ...strideResponse('pending'),
              memoGifId: 'wNlks0ID1igO4',
            })
          );
        }
        // 1 Click Claim
        case '10': {
          numberOfCalls++;
          const isPolling = numberOfCalls > 1;
          return isPolling
            ? res(ctx.json(strideResponse('succeeded')))
            : res(
                ctx.json({
                  ...strideResponse('pending'),
                  previousClaim: {
                    lastFour: '1234',
                    firstName: 'Hat',
                    externalCardId: '5678901234',
                  },
                })
              );
        }
        // Retryable Claim - Failed transaction
        case '11': {
          numberOfCalls++;
          const isPolling = numberOfCalls > 1;
          return isPolling
            ? res(ctx.json(strideResponse('failed')))
            : res(ctx.json(strideResponse('pending')));
        }
        // Retryable Claim - Reversed transaction
        case '12': {
          numberOfCalls++;
          const isPolling = numberOfCalls > 1;
          return isPolling
            ? res(ctx.json(strideResponse('reversed')))
            : res(ctx.json(strideResponse('pending')));
        }
        // Retryable Claim - Retryable transaction
        case '13': {
          numberOfCalls++;

          if (isFormSubmitted) {
            const isFirstPollingCall = numberOfCalls === 2;

            return isFirstPollingCall
              ? res(ctx.json(strideResponse('pending')))
              : res(ctx.json(strideResponse('succeeded')));
          }

          return res(ctx.json(strideResponse('pending')));
        }
        // Retryable Claim - Delayed success transaction
        case '14': {
          numberOfCalls++;
          const isPolling = numberOfCalls > 1;
          return isPolling
            ? res(ctx.json(strideResponse('sent')))
            : res(ctx.json(strideResponse('pending')));
        }

        default:
          return res(
            ctx.status(404),
            ctx.json({
              success: false,
              error: { id: 404, message: 'Resource Not Found' },
            })
          );
      }
    }
  ),

  //graphql version of card_encryption_keys endpoint mentioned above
  graphql.query('P2pCardEncryptionKeys', (req, res, ctx) => {
    // When authenticated, respond with a query payload
    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            card_encryption_keys: [
              {
                id: '1234',
                type: 'tabapay',
                name: 'tabapay:stride',
                key: window.atob(process.env.TABAPAY_PUBLIC_KEY_EXAMPLE),
                __typename: 'P2pCardEncryptionKey',
              },
              {
                id: '5678',
                type: 'tabapay',
                name: 'tabapay:bancorp',
                key: window.atob(process.env.TABAPAY_PUBLIC_KEY_EXAMPLE),
                __typename: 'P2pCardEncryptionKey',
              },
            ],
            __typename: 'P2pUnauthenticatedQueryRoot',
          },
          __typename: 'UnauthenticatedQueryRoot',
        },
      })
    );
  }),

  graphql.query('PayAnyoneByTokenResponseConfig', (req, res, ctx) => {
    // When authenticated, respond with a query payload
    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            pay_anyone_client_config: {
              maintenance_mode_enabled: false,
              ocr_enabled: true,
              graphql_usage: {
                use_gql_get_and_claim_pay_anyone: false,
                __typename: 'PayAnyoneClientGraphqlUsage',
              },
              __typename: 'PayAnyoneClientConfig',
            },
            __typename: 'P2pUnauthenticatedQueryRoot',
          },
          __typename: 'UnauthenticatedQueryRoot',
        },
      })
    );
  }),

  // Success: pf endpoint on load of page
  graphql.query('PayAnyoneByTokenResponse', (req, res, ctx) => {
    // When authenticated, respond with a query payload
    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            pay_anyone_client_config: {
              maintenance_mode_enabled: false,
              ocr_enabled: true,
              graphql_usage: {
                use_gql_get_pay_friend_by_token: false,
                use_gql_guest_claim_pay_anyone: false,
                use_gql_get_and_claim_pay_anyone: false,
                __typename: 'PayAnyoneClientGraphqlUsage',
              },
              __typename: 'PayAnyoneClientConfig',
            },
            pay_anyone: {
              __typename: 'PayAnyoneWithExternalCard',
              pay_friend: {
                id: 'EysKMKkbN',
                ux_status: 'PENDING',
                amount: 10.0,
                memo: ':100::fire:',
                memo_gif_id: '',
                public_id: '5a69627d-625e-4404-8033-4e6ed09f6148',
                expires_at: '2024-04-18T18:59:59Z',
                receiver_identifier: 'pavi.krishna+thursday@chime.com',
              },
              previous_claim: null,
              sender: {
                bank: 'stride',
                referral_token: 'staceyylin',
                name: 'Mickey Mouse',
                id: 'VERIx6ME',
              },
              recipient_previously_claimed: false,
              claim_with_chime: true,
            },
          },
        },
      })
    );
  }),

  //Success: one click claim
  // graphql.query('PayAnyoneByTokenResponse', (req, res, ctx) => {
  //   // When authenticated, respond with a query payload
  //   return res(
  //     ctx.data({
  //       unauthenticated: {
  //         p2p: {
  //           pay_anyone_client_config: {
  //             maintenance_mode_enabled: false,
  //             ocr_enabled: true,
  //             graphql_usage: {
  //               use_gql_get_pay_friend_by_token: false,
  //               use_gql_guest_claim_pay_anyone: false,
  //               __typename: 'PayAnyoneClientGraphqlUsage',
  //             },
  //             __typename: 'PayAnyoneClientConfig',
  //           },
  //           pay_anyone: {
  //             __typename: 'PayAnyoneWithExternalCard',
  //             pay_friend: {
  //               id: 'EysKMKkbN',
  //               ux_status: 'pending',
  //               amount: 10.0,
  //               memo: ':100::fire:',
  //               memo_gif_id: '',
  //               public_id: '5a69627d-625e-4404-8033-4e6ed09f6148',
  //               expires_at: '2024-04-18T18:59:59Z',
  //               receiver_identifier: 'pavi.krishna+thursday@chime.com',
  //             },
  //             previous_claim: {
  //               external_card_id: '5678901234',
  //               first_name: 'Hat',
  //               last_four: '1234',
  //             },
  //             sender: {
  //               bank: 'stride',
  //               referral_token: 'staceyylin',
  //               name: 'Mickey Mouse',
  //               id: 'VERIx6ME',
  //             },
  //             recipient_previously_claimed: false,
  //             claim_with_chime: true,
  //           },
  //         },
  //       },
  //     })
  //   );
  // }),

  // Failure: step up required pf show endpoint on load of page
  // graphql.query('PayAnyoneByTokenResponse', (req, res, ctx) => {
  //   // When authenticated, respond with a query payload
  //   return res(
  //     ctx.data({
  //       unauthenticated: {
  //         p2p: {
  //           pay_anyone_client_config: {
  //             maintenance_mode_enabled: false,
  //             ocr_enabled: true,
  //             graphql_usage: {
  //               use_gql_get_pay_friend_by_token: false,
  //               use_gql_guest_claim_pay_anyone: true,
  //               __typename: 'PayAnyoneClientGraphqlUsage',
  //             },
  //             __typename: 'PayAnyoneClientConfig',
  //           },
  //           pay_anyone: {
  //             __typename: 'StepUpRequiredError',
  //             title: 'Step Up Required',
  //             message: 'This action requires further authentication.',
  //             challenge_context:
  //               'AQICAHhMG1VBPVt_r--6Srb_W-9d8tKLdWX40LsTaA2IUF8UjgFfva_N09L_qJ15no7pdsT7AAABOjCCATYGCSqGSIb3DQEHBqCCAScwggEjAgEAMIIBHAYJKoZIhvcNAQcBMB4GCWCGSAFlAwQBLjARBAygKImvPO7kEaxMzHICARCAge53uafeO5PEEUg96ZzG6-tPxi4-tHbOvQai-nZobkyGPM4oFx796CSNpBa_TIbkPzaD55ijvewIdTuglE64p5VE6TBvi3nR7jOfvg4jc5VDEcNaDYOTJKWdG9rr4OCB7LARWegI1PIneCLh8jClmutHDARZ9zIygQDybZqMCSFRVZwf_xFkq_TfMLp7ekS9PNxFNEc7T2Pq4zO3sV25A3jz5gpY9PBB61dFUkh5NC18rLEPQOCsrbI9rjAQTzwTPiMBNg1JwBrwKcU5CKfk2DRFJABv8BLRdQnbM_dLpuwwnE8gI9ry6QoBpF15E9U1',
  //           },
  //         },
  //       },
  //     })
  //   );
  // }),

  // Failure: fetch error pf show endpoint on load of page
  // graphql.query('PayAnyoneByTokenResponse', (req, res, ctx) => {
  //   // When authenticated, respond with a query payload
  //   return res(
  //     ctx.data({
  //       unauthenticated: {
  //         p2p: {
  //           pay_anyone_client_config: {
  //             maintenance_mode_enabled: false,
  //             ocr_enabled: true,
  //             graphql_usage: {
  //               use_gql_get_pay_friend_by_token: true,
  //               use_gql_guest_claim_pay_anyone: true,
  //               __typename: 'PayAnyoneClientGraphqlUsage',
  //             },
  //             __typename: 'PayAnyoneClientConfig',
  //           },
  //           pay_anyone: {
  //             __typename: 'PayAnyoneFetchError',
  //             error_id: 'generic',
  //             error_header: 'Something went wrong',
  //             error_body:
  //               "We're working on it. Please try again later or contact our Member Services team if you need more support.",
  //           },
  //         },
  //       },
  //     })
  //   );
  // }),

  // {
  //     "errors": [
  //         {
  //             "message": "Field 'referral_fine_print' doesn't exist on type 'PayFriendsSenderInfo'",
  //             "locations": [
  //                 {
  //                     "line": 41,
  //                     "column": 13
  //                 }
  //             ],
  //             "path": [
  //                 "query PayAnyoneByTokenResponse",
  //                 "unauthenticated",
  //                 "p2p",
  //                 "pay_anyone",
  //                 "... on PayAnyoneWithExternalCard",
  //                 "sender",
  //                 "referral_fine_print"
  //             ],
  //             "extensions": {
  //                 "code": "undefinedField",
  //                 "typeName": "PayFriendsSenderInfo",
  //                 "fieldName": "referral_fine_print"
  //             }
  //         }
  //     ]
  // }

  graphql.query('PayAnyoneByTokenResponsePolling', (req, res, ctx) => {
    // When authenticated, respond with a query payload
    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            pay_anyone: {
              __typename: 'PayAnyoneWithExternalCard',
              pay_friend: {
                id: 'EysKMKkbN',
                ux_status: 'succeeded',
              },
            },
          },
        },
      })
    );
  }),

  // Sucess: claim endpoint with saved debit response
  graphql.mutation('PayAnyoneClaimSavedDebitResponse', (_, res, ctx) => {
    // When authenticated, respond with a query payload
    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            claim_pay_anyone: {
              __typename: 'ClaimPayAnyoneResult',
              success: true,
              token: '3N6pLxtCcpHxMsSm6CcwQBH',
            },
          },
        },
      })
    );
  }),

  // Failure: claim error
  // graphql.mutation('PayAnyoneClaimSavedDebitResponse', (_, res, ctx) => {
  //   // When authenticated, respond with a query payload
  //   return res(
  //     ctx.data({
  //       unauthenticated: {
  //         p2p: {
  //           claim_pay_anyone: {
  //             __typename: 'PayAnyoneClaimError',
  //             error_id: 'claim_in_progress',
  //             error_body:
  //               "We're working on it. Please try again later or contact our Member Services team if you need more support.",
  //             error_header: 'Something went wrong',
  //           },
  //         },
  //       },
  //     })
  //   );
  // }),

  // Success: claim without saving debit card
  graphql.mutation('PayAnyoneClaimNotSavedDebitResponse', (_, res, ctx) => {
    // When authenticated, respond with a query payload
    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            claim_pay_anyone_with_saved_debit_card: {
              __typename: 'ClaimPayAnyoneResult',
              success: true,
              token: '3N6pLxtCcpHxMsSm6CcwQBH',
            },
          },
        },
      })
    );
  }),

  // Failure: saved debit card response
  // graphql.mutation('PayAnyoneClaimSavedDebitResponse', (_, res, ctx) => {
  //   // When authenticated, respond with a query payload
  //   return res(
  //     ctx.data({
  //       unauthenticated: {
  //         p2p: {
  //           claim_pay_anyone_with_saved_debit_card: {
  //             __typename: 'PayAnyoneClaimError',
  //             error_id: 'claim_in_progress',
  //             error_body:
  //               "We're working on it. Please try again later or contact our Member Services team if you need more support.",
  //             error_header: 'Something went wrong',
  //           },
  //         },
  //       },
  //     })
  //   );
  // }),

  graphql.mutation('UnauthenticatedCreateStepUp', (_, res, ctx) => {
    return res(
      ctx.data({
        unauthenticated: {
          step_up: {
            create_step_up: {
              __typename: 'CreateStepUpResult',
              id: '12ecb733-62ab-411f-8dfe-0fe32073edf3',
              step_up_challenge_type: 'SECURE_3D',
              step_up_challenge_vendor: 'CARDINAL',
              step_up_status: 'CREATED',
            },
            __typename: 'StepUpMutation',
          },
          __typename: 'UnauthenticatedMutation',
        },
      })
    );
  }),

  graphql.mutation('UnauthenticatedInitialize3DS', (_, res, ctx) => {
    return res(
      ctx.data({
        unauthenticated: {
          step_up: {
            initialize_3ds: {
              __typename: 'StepUpInitialize3DSResult',
              id: '12ecb733-62ab-411f-8dfe-0fe32073edf3',
              pre_challenge_jwt:
                'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJPYmplY3RpZnlQYXlsb2FkIjpmYWxzZSwiT3JnVW5pdElkIjoiNjAxZGIyZGIzZTJiYzQxN2I4NjNjYTIxIiwiUGF5bG9hZCI6IntcIk9yZGVyRGV0YWlsc1wiOntcIk9yZGVyTnVtYmVyXCI6XCIwNGQ1NGU5ZC1kNGEzLTQwOGItYWUzZi1jMWY2MGU2ODIzYmRcIixcIkFtb3VudFwiOlwiMTIzNFwifX0iLCJSZWZlcmVuY2VJZCI6IjM4ZGI2MDMxLThlMmUtNDU5My04Y2FiLTM3OGFhNDBhOWY1NSIsImlhdCI6MTY5MDM0Mzg5MC44NzkxNTIsImlzcyI6IjYwMjE2ODg5MzE2MTQ2MzNjMDkwNzUzZCIsImp0aSI6IjIwMjMwNzIyMTgxMTE3MDAxMzIifQ.c97MGU-tcFXDLvJGFPguFoj0vezCDZAuShJcly24NWg',
              step_up_status: 'CHALLENGE_INITIALIZED',
            },
            __typename: 'StepUpMutation',
          },
          __typename: 'UnauthenticatedMutation',
        },
      })
    );
  }),

  graphql.mutation('UnauthenticatedLookUp3DS', (_, res, ctx) => {
    return res(
      ctx.data({
        unauthenticated: {
          step_up: {
            look_up_3ds: {
              __typename: 'ChallengeNotRequired3DSResult',
              id: '6de21382-2964-49c7-b450-2b8c9996082b',
              challenge_context:
                'AQICAHilKGrvKAeV2WZnKkNUKLs-aPUY_y09CDpVZou3NEsRWQGtUq2LoaGJz4x4pp0g6F_LAAACYjCCAl4GCSqGSIb3DQEHBqCCAk8wggJLAgEAMIICRAYJKoZIhvcNAQcBMB4GCWCGSAFlAwQBLjARBAyJz1a-DwUgFtCWrwUCARCAggIVD7hf5BJTPb04_mOViWq2JoBor1jt6OcQtQ30IDHQXRSHZDf64bvAhaKF9Gak7MJvI1mAU_kjBpV2OwLH3JuAt9u-5_aghCZzVnLf2SvQIC1XZGHkSwUqLlwCPSYgKnSD6Z4_GzZVmxJePt5lH2JPwWPt6SWCksej1NMfVljQ-KYcct6Vq5tM1VALWQ3mOmYvuve_DTvdYQ1G7Djympk5CVvOkZZ8ZyTpgxRoiDO5WoUhjUGFw87MwJlM2Q0R8c80ZZrVC5CnDFsBsrBBGUTOUfrByj6IY-oZxdYLIGidtzotU-vyjNmBXpJXVMVM9-utcfBiFoBxLCk6OzRIpnvjNqP4wly2t4Y1Bf4IjNIgo11k2woQx_qOslMX3y2rB2Q3m5ZYMBCu_Ds3HaXRtqJE_UndPLpk__gBIzZRAnm0d8L-_oLcXnlD9x8x969t582c1qjD6pqAd51wsWjGKNXDI9uzjJX2z9irZaix9hTDcIAM90oCTyO9acirD3uR7dNqhaCQGub2I2opUhjfJCOg-oRuVknMOseFV5SkaJ1jWQzZBCATNWtXAv4pIkW4u1n8sASrlFmGqewNRnxu0CkJRwfz0jOXDCkFQ2jYHig4vKSTBJZlkejwzEuIOHQY_ZUbgvO7j00z40yjzxP88jsLAbzcbV_g6jy4G1AOZTd2HcDsChUwXTebB_XexnDdCHiH_Sutvzk=',
              step_up_status: 'CHALLENGE_NOT_REQUIRED',
            },
            __typename: 'StepUpMutation',
          },
          __typename: 'UnauthenticatedMutation',
        },
      })
    );
  }),

  graphql.mutation('UnauthenticatedAuthenticate3DS', (_, res, ctx) => {
    return res(
      ctx.data({
        unauthenticated: {
          step_up: {
            authenticate_3ds: {
              __typename: 'StepUpAuthenticate3DSResult',
              id: '824c496e-fe49-4829-a39c-1435ffd327fa',
              challenge_context:
                'AQICAHilKGrvKAeV2WZnKkNUKLs-aPUY_y09CDpVZou3NEsRWQEGw1_KAE0usq3N8444Sfn5AAACYzCCAl8GCSqGSIb3DQEHBqCCAlAwggJMAgEAMIICRQYJKoZIhvcNAQcBMB4GCWCGSAFlAwQBLjARBAwlYMKwOLxZzwC86U4CARCAggIW6fWeL64Kxhv5yzuIvHg3Odc4FYgPgrAGpai2kLUHz4Dsz3nZnxvJNZKHLCRJn6Kn_R1iqA-q3N_lNfeNdyU4SBMPN7rCvVQfGkC13crG3Pp5YFYAUM9RV8-He48ubOM3Ib7KH4DLlfahk8ETOYKnk80oCg621uvDcRNl8iiZ9ANS5Limi_7JLYe_Z8LzXISSDs3aXxgcYjcO8HV8XZmoQodPHt8dgwDmhzIIq-Hh1MiS15TjuQyITUIPC9TGFWDYUbF-88o6Nxh7kUY2F2wOdOSNFuCaUwFth5KzEJPw_Jqr1tWqX6-wowS95a2V4OCuaoggA7IOjnooLMHol3VG-puJjiXLtGLC_6wO-f4locNCspsI37KCQ4rsFY1eqO9shKM69rhgVqlVcsGfRXacMwLwfVYANgF6GlVaM9dxx5-nRjXbiW5BIfwO61dOuYs1fuSD8sxWq1sQiRmvmWEj1KHcYavPeq2_zdFpQ3XwkzFCGnzpA2eq6A18sQUzumRwPKoLMW4so0fDGsvipxvZwbxhhtYDdZEqLgqRhUcNs93pw2WsD2iUUgXy15EQiXdBoEBgqOFapFWzInskYYt5NxxX2Uci9RoYLu4gop7RvyYHL7cqGqe69Pb4eHgPoVpwS8iRkARZ6A3dZmkHGVg5dNyLwYWJ7g4FG8JHAQlJc27QsYV3tuCktGSHKE1ALBENwZCNWoo1',
              step_up_status: 'CHALLENGE_AUTHENTICATED',
            },
            __typename: 'StepUpMutation',
          },
          __typename: 'UnauthenticatedMutation',
        },
      })
    );
  }),

  rest.post(
    'https://centinelapistag.cardinalcommerce.com/V1/Order/JWT/Init',
    (_, res, ctx) => {
      return res(
        ctx.json({
          CardinalJWT:
            'eyJhbGciOiJIUzI1NiJ9.eyJpc3MiOiI2MDIxNjg4OTMxNjE0NjMzYzA5MDc1M2QiLCJpYXQiOjE2OTA0MDAxNDQsImV4cCI6MTY5MDQwNzM0NCwianRpIjoiYmZlNTAzOWEtOTE4Ni00NTg4LThkOTYtNjU5NGRlNTgyYWQ2IiwiQ29uc3VtZXJTZXNzaW9uSWQiOiIwXzA0Mzg0ZmU2LWNjM2MtNDFmNS04MzliLTQ3NzdlNTEyYTdmYSIsIlJlZmVyZW5jZUlkIjoiNGZjMWU1YzYtMTRhNi00ODIwLWFkOTMtMDAwY2ZmOTczNWEwIiwiYXVkIjoiMjAyMzA3MjIxODExMTcwMDE2NCIsIlBheWxvYWQiOiJ7XCJEZXZpY2VGaW5nZXJwcmludGluZ1VSTFwiOlwiaHR0cHM6Ly9nZW9zdGFnLmNhcmRpbmFsY29tbWVyY2UuY29tL0RldmljZUZpbmdlcnByaW50V2ViL1YyL0Jyb3dzZXIvUmVuZGVyP3JlZmVyZW5jZUlkXFx1MDAzZDRmYzFlNWM2LTE0YTYtNDgyMC1hZDkzLTAwMGNmZjk3MzVhMFxcdTAwMjZvcmdVbml0SWRcXHUwMDNkNjAxZGIyZGIzZTJiYzQxN2I4NjNjYTIxXFx1MDAyNnRocmVhdG1ldHJpeFxcdTAwM2R0cnVlXFx1MDAyNnRtRXZlbnRUeXBlXFx1MDAzZFBBWU1FTlRcXHUwMDI2Z2VvbG9jYXRpb25cXHUwMDNkZmFsc2VcXHUwMDI2YWxpYXNcXHUwMDNkRGVmYXVsdFwiLFwiVVJMc1wiOntcIkRldmljZUZpbmdlcnByaW50XCI6e1wiQmFzZVwiOlwiaHR0cHM6Ly9nZW9zdGFnLmNhcmRpbmFsY29tbWVyY2UuY29tL0RldmljZUZpbmdlcnByaW50V2ViL1YyXCIsXCJRdWVyeVBhcmFtZXRlcnNcIjp7XCJ0aHJlYXRtZXRyaXhcIjpcInRydWVcIixcImFsaWFzXCI6XCJEZWZhdWx0XCIsXCJvcmdVbml0SWRcIjpcIjYwMWRiMmRiM2UyYmM0MTdiODYzY2EyMVwiLFwidG1FdmVudFR5cGVcIjpcIlBBWU1FTlRcIixcInJlZmVyZW5jZUlkXCI6XCI0ZmMxZTVjNi0xNGE2LTQ4MjAtYWQ5My0wMDBjZmY5NzM1YTBcIixcImdlb2xvY2F0aW9uXCI6XCJmYWxzZVwifX0sXCJIb3N0ZWRGaWVsZHNcIjpcImh0dHBzOi8vaG9zdGVkZmllbGRzc3RhZy5jYXJkaW5hbGNvbW1lcmNlLmNvbS9Ib3N0ZWRGaWVsZHNXZWIvVjFcIn0sXCJFbmFibGVkQXBwbGVQYXlcIjpmYWxzZSxcIkVuYWJsZWRDQ0FcIjp0cnVlLFwiRW5hYmxlZERpc2NvdmVyXCI6ZmFsc2UsXCJFbmFibGVkSG9zdGVkRmllbGRzXCI6ZmFsc2UsXCJFbmFibGVkUGF5cGFsXCI6ZmFsc2UsXCJFbmFibGVkVmlzYUNoZWNrb3V0XCI6ZmFsc2UsXCJGZWF0dXJlRmxhZ3NcIjp7XCJFbmFibGVkU3VwcHJlc3NlZFBheW1lbnRzVmFsaWRhdGVkUmVzcG9uc2VcIjpmYWxzZSxcIkJpblByZWxvYWRFbmFibGVkXCI6dHJ1ZX0sXCJPcHRpbWl6ZWx5RmxhZ3NcIjp7XCJTb25nYmlyZFBvc3RNZXNzYWdlVmFsaWRhdGlvblwiOmZhbHNlfSxcIkVycm9yTnVtYmVyXCI6MCxcIkVycm9yRGVzY3JpcHRpb25cIjpcIlN1Y2Nlc3NcIn0ifQ.jAlUbAzlq1iPg0nlIrL4-wovl5KtFLY_SRQrqu-axDo',
        })
      );
    }
  ),

  rest.post(
    'https://centinelapistag.cardinalcommerce.com/V1/Order/JWT/Continue',
    (_, res, ctx) => {
      return res(
        ctx.json({
          CardinalJWT:
            'eyJhbGciOiJIUzI1NiJ9.eyJpc3MiOiI2MDIxNjg4OTMxNjE0NjMzYzA5MDc1M2QiLCJpYXQiOjE2OTA0MDgyNzcsImV4cCI6MTY5MDQxNTQ3NywianRpIjoiYTRjZTBhNTMtYmRiZC00ZDY3LTg0ZGEtNzZhNDQwNDU1OGZlIiwiQ29uc3VtZXJTZXNzaW9uSWQiOiIwXzA0Mzg0ZmU2LWNjM2MtNDFmNS04MzliLTQ3NzdlNTEyYTdmYSIsIlJlZmVyZW5jZUlkIjoiNDdjZjZjOTItMTgxMy00YjE1LWJiYmYtOWU0NDE4ZGUzZmUxIiwiYXVkIjoiMjAyMzA3MjIxODExMTcwMDE2NyIsIlBheWxvYWQiOiJ7XCJFcnJvck51bWJlclwiOjAsXCJFcnJvckRlc2NyaXB0aW9uXCI6XCJTdWNjZXNzXCJ9In0.93JyNeN1I3UKkYdx_sZ595quzqzvNIkM0LqWDEszlfw',
        })
      );
    }
  ),

  rest.post('https://writer.cardinalcommerce.com/stag/log', (_, res, ctx) => {
    return res(ctx.json({ status: 'ok' }));
  }),

  rest.get(
    `${process.env.CONSUMER_HOST}/mr/:token.json`,
    ({ params: { token } }, res, ctx) => {
      const buildTokenResponseWithBank = buildTokenInfo(token);
      const strideResponse = buildTokenResponseWithBank('stride');
      const bancorpResponse = buildTokenResponseWithBank('bancorp');
      const tokenWithReuseDebitValues = Number(token) >= 11111 ? '10' : token;

      // Pay Anyone status is only available after the user posts
      function createMoneyRequestPostResponse(
        moneyRequestStatus: MoneyRequestStatus,
        payAnyoneStatus: PayAnyoneStatus
      ): any {
        return {
          ...strideResponse(MoneyRequestStatus.pending),
          payfriendStatus: payAnyoneStatus,
        };
      }

      switch (tokenWithReuseDebitValues) {
        // Polling fetch
        case '1': {
          numberOfCalls++;

          if (isFormSubmitted) {
            const isPollingComplete = numberOfCalls > 3;

            return isPollingComplete
              ? res(
                  ctx.json(
                    createMoneyRequestPostResponse(
                      MoneyRequestStatus.accepted,
                      PayAnyoneStatus.succeeded
                    )
                  )
                )
              : res(
                  ctx.json(
                    createMoneyRequestPostResponse(
                      MoneyRequestStatus.pending,
                      PayAnyoneStatus.sent
                    )
                  )
                );
          }

          return res(ctx.json(strideResponse('pending')));
        }
        // Bancorp Polling Fetch
        case '2':
          numberOfCalls++;

          if (isFormSubmitted) {
            const isPollingComplete = numberOfCalls > 3;

            return isPollingComplete
              ? res(
                  ctx.json({
                    ...bancorpResponse(MoneyRequestStatus.accepted),
                    payfriendStatus: PayAnyoneStatus.succeeded,
                  })
                )
              : res(
                  ctx.json({
                    ...bancorpResponse(MoneyRequestStatus.pending),
                    payfriendStatus: PayAnyoneStatus.sent,
                  })
                );
          }

          return res(ctx.json(bancorpResponse('pending')));
        // Errors
        case '3':
          return res(ctx.json(bancorpResponse(MoneyRequestStatus.expired)));
        case '4':
          return res(
            ctx.json(
              createMoneyRequestPostResponse(
                MoneyRequestStatus.accepted,
                PayAnyoneStatus.succeeded
              )
            )
          );
        case '5':
          return res(ctx.json(bancorpResponse(MoneyRequestStatus.cancelled)));
        case '6':
          return res(ctx.json(bancorpResponse(MoneyRequestStatus.declined)));
        case '7':
          return res(ctx.json(bancorpResponse(MoneyRequestStatus.ignored)));
        // Money Request with a gif
        case '9': {
          return res(
            ctx.json({
              ...strideResponse('pending'),
              memoGifId: 'wNlks0ID1igO4',
            })
          );
        }
        // Retryable Claim - Failed transaction
        case '11': {
          numberOfCalls++;
          const isPolling = numberOfCalls > 1;
          return isPolling
            ? res(
                ctx.json(
                  createMoneyRequestPostResponse(
                    MoneyRequestStatus.pending,
                    PayAnyoneStatus.failed
                  )
                )
              )
            : res(ctx.json(strideResponse('pending')));
        }
        // Retryable Claim - Reversed transaction
        case '12': {
          numberOfCalls++;
          const isPolling = numberOfCalls > 1;
          return isPolling
            ? res(
                ctx.json(
                  createMoneyRequestPostResponse(
                    MoneyRequestStatus.pending,
                    PayAnyoneStatus.reversed
                  )
                )
              )
            : res(ctx.json(strideResponse('pending')));
        }
        // Retryable Claim - Retryable transaction
        case '13': {
          numberOfCalls++;

          if (isFormSubmitted) {
            const isFirstPollingCall = numberOfCalls === 2;
            return isFirstPollingCall
              ? res(
                  ctx.json(
                    createMoneyRequestPostResponse(
                      MoneyRequestStatus.pending,
                      PayAnyoneStatus.pending
                    )
                  )
                )
              : res(
                  ctx.json(
                    createMoneyRequestPostResponse(
                      MoneyRequestStatus.accepted,
                      PayAnyoneStatus.succeeded
                    )
                  )
                );
          }
          return res(ctx.json(strideResponse('pending')));
        }
        // Retryable Claim - Delayed success transaction
        case '14': {
          numberOfCalls++;
          const isPolling = numberOfCalls > 1;
          return isPolling
            ? createMoneyRequestPostResponse(
                MoneyRequestStatus.pending,
                PayAnyoneStatus.sent
              )
            : res(ctx.json(strideResponse('pending')));
        }
        // Polling fetch
        case '100': {
          numberOfCalls++;

          if (isFormSubmitted) {
            const isFirstPollingComplete = numberOfCalls === 4;
            const isSecondPollingComplete = numberOfCalls > 7;
            if (isFirstPollingComplete) {
              return res(
                ctx.status(428),
                ctx.json({
                  error_id: 'step_up_required_error',
                  error_header: 'Step up required',
                  error_body: '',
                  step_up_challenge_context:
                    'AQICAHilKGrvKAeV2WZnKkNUKLs-aPUY_y09CDpVZou3NEsRWQHxraOSaEgIh9wrUMYExA1VAAAByzCCAccGCSqGSIb3DQEHBqCCAbgwggG0AgEAMIIBrQYJKoZIhvcNAQcBMB4GCWCGSAFlAwQBLjARBAwA5IvwT--VvTdw3qkCARCAggF-4qn4fiZSOOgHvVz_gaVXO7fyrlGWtExFHiz1_WRhUMkLEwdCYw6RMriG6bI-AqCZk6U7nLIG9o_0GvPJGBPI3VDgsbFuPRKtpuUPVmJa7QXXODmxRKtamhi1tkFzSQT0zNMvyVxr2CkfVDG5QwCCUFpNis6dJWIhhSqvlK_5PLMfk_ikwhPA5cpmgR08xKy1FiWcKuh2WdtK_XV-_X_JtOwDLZnBFp8HNcxmdPFzy9jNw81atRPS0MhCP4e36uidUuhyhNAS3qgUlCWoJEBDwrA7WrJV1QaSl_UCZtxAWNbQPunttHfSCm7om36Pa3VOjP38WyfjgdU7ro76JJt89IKn86zMCYbeqdoRlcfOPl7ZKpIib1VfQr6NcdGWAMAU13MJW3NwnZqrJMnr9MWn0BqH7TXsORQQVPIrdZXj0Pts1R__-ovIyskCR1wLBnBOjigvo4SfucvDuaIAayoGcWM5AEjv9PqhvYP4Suedb1kIlvCymX3nvQMVZ7joag==',
                })
              );
            } else if (isSecondPollingComplete) {
              return res(
                ctx.json(
                  createMoneyRequestPostResponse(
                    MoneyRequestStatus.accepted,
                    PayAnyoneStatus.succeeded
                  )
                )
              );
            } else {
              return res(
                ctx.json(
                  createMoneyRequestPostResponse(
                    MoneyRequestStatus.pending,
                    PayAnyoneStatus.sent
                  )
                )
              );
            }
          }

          return res(ctx.json(strideResponse('pending')));
        }

        default:
          return res(
            ctx.status(404),
            ctx.json({
              success: false,
              error: { id: 404, message: 'Resource Not Found' },
            })
          );
      }
    }
  ),

  /**
   * Matches:
   * Pay Anyone: /pf/claim/
   * Receive from Anyone: /mr/pay/
   */
  rest.post(`${process.env.CONSUMER_HOST}/*/*/:token`, claimInfoPostResolver),

  rest.post(
    `${process.env.CONSUMER_HOST}/mobile/claim/:userId`,
    claimInfoPostResolver
  ),

  graphql.query('UsePayAnyoneIncentiveReferralTokenQuery', (_, res, ctx) => {
    return res(
      ctx.data({
        unauthenticated: {
          p2p: {
            pay_anyone_referral: {
              // /*
              //   Example of a expected return
              // */
              __typename: 'P2pPayAnyoneReferral',
              amount: 15.0,
              expires_at: '2029-03-19T03:37:25Z',
              token: '1',
              ready_to_claim: false,
              sender_name: 'Sender Name',
              sender_referral_token: 'senderReferralToken',

              /*
                Error state
              */
              // __typename: 'GenericError',
              // title: 'error',
              // message: 'error',
            },
            __typename: 'P2pUnauthenticatedQueryRoot',
          },
          __typename: 'UnauthenticatedQueryRoot',
        },
      })
    );
  }),
];
